import { useAuth0 } from '@auth0/auth0-react';
import { type ComponentType } from 'react';

/** Auth0's OAuthError carries the OAuth error `code` alongside the message. */
interface OAuthLikeError extends Error {
  error?: string;
}

/** App identity shown on the error screen — matches the sidebar brand. */
export interface AuthErrorBrand {
  title: string;
  /** Lucide (or any) icon component; rendered in the brand square. */
  icon?: ComponentType<{ className?: string }>;
}

/**
 * Shown by {@link RequireAuth} when the Auth0 redirect comes back with an error
 * instead of a session — most importantly `access_denied`, which the PostLogin
 * Action returns when the user isn't subscribed to this app. Without this the
 * app would just re-trigger `loginWithRedirect` and loop forever.
 *
 * Mirrors the Auth0 universal-login layout (white card centred on a dark navy
 * backdrop) so it reads as a continuation of the sign-in flow. Gives the user a
 * way out: log out of Auth0 (to sign in with an account that *is* subscribed),
 * plus a Try again for transient (non-access) errors.
 */
export function AuthError({ error, brand }: { error: Error; brand?: AuthErrorBrand }) {
  const { logout, loginWithRedirect } = useAuth0();
  const accessDenied = (error as OAuthLikeError).error === 'access_denied';
  const BrandIcon = brand?.icon;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-800 px-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl sm:p-10">
        {/* Brand lockup — the app's "logo", matching the sidebar brand. */}
        <div className="mb-7 flex items-center justify-center gap-2.5">
          {BrandIcon && (
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white shadow-sm">
              <BrandIcon className="h-5 w-5" />
            </span>
          )}
          <span className="text-lg font-semibold tracking-tight text-slate-800">
            {brand?.title ?? 'My Suite'}
          </span>
        </div>

        {/* State badge — lock for access-denied, amber triangle otherwise. */}
        <div
          className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
            accessDenied ? 'bg-slate-100 text-[var(--color-primary)]' : 'bg-amber-50 text-amber-500'
          }`}
        >
          {accessDenied ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6"
              aria-hidden="true"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6"
              aria-hidden="true"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          )}
        </div>

        <h1 className="text-xl font-semibold text-slate-900">
          {accessDenied ? 'You don’t have access' : 'We couldn’t sign you in'}
        </h1>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
          {error.message}
        </p>

        <div className="mt-7 space-y-2">
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="w-full rounded-lg bg-[var(--color-primary)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            Log out
          </button>
          {!accessDenied && (
            <button
              type="button"
              onClick={() => void loginWithRedirect()}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Try again
            </button>
          )}
        </div>

        {accessDenied && (
          <p className="mt-5 text-xs text-slate-400">
            Sign in with an account that’s subscribed to this app.
          </p>
        )}
      </div>
    </div>
  );
}
